import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatTreeModule } from '@angular/material/tree';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatListModule } from '@angular/material/list';
import { MatCardModule } from '@angular/material/card';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatBadgeModule } from '@angular/material/badge';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
// 添加拖放模組的導入
import { DragDropModule, CdkDragDrop } from '@angular/cdk/drag-drop';

import { DepartmentService } from '../../../services/department.service';
import { Department, DepartmentNode } from '../../../models/department.model';
import { User } from '../../../models/user.model';

// Interface for flat node (for MatTree)
interface FlatDepartmentNode {
  expandable: boolean;
  name: string;
  level: number;
  id: string;
  userCount: number;
}

@Component({
  selector: 'app-dep-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatTreeModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatDialogModule,
    MatListModule,
    MatCardModule,
    MatTooltipModule,
    MatBadgeModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatSelectModule,
    MatSnackBarModule,
    DragDropModule  // 添加拖放模組
  ],
  templateUrl: './dep-management.component.html',
  styleUrl: './dep-management.component.scss'
})
export class DepManagementComponent implements OnInit {
  // Department tree related
  private _transformer = (node: DepartmentNode, level: number): FlatDepartmentNode => {
    return {
      expandable: !!node.children && node.children.length > 0,
      name: node.name,
      level: level,
      id: node.id,
      userCount: node.userCount
    };
  };

  treeControl = new FlatTreeControl<FlatDepartmentNode>(
    node => node.level,
    node => node.expandable
  );

  treeFlattener = new MatTreeFlattener(
    this._transformer,
    node => node.level,
    node => node.expandable,
    node => node.children
  );

  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

  // Selected department and its users
  selectedDepartment: Department | null = null;
  departmentUsers: User[] = [];
  nonDepartmentUsers: User[] = [];

  // New department properties
  showAddDepartmentForm: boolean = false;
  newDepartmentName: string = '';
  newDepartmentParentId: string | null = null;

  // Edit department properties
  editingDepartment: boolean = false;
  editDepartmentName: string = '';

  // Add user properties
  showAddUserForm: boolean = false;
  selectedUserToAdd: string = '';

  // Loading states
  isLoading: boolean = false;

  // 追蹤展開的節點
  expandedNodes = new Set<string>();

  // 拖放相關屬性
  draggedNode: DepartmentNode | null = null;
  allowDrop = false;
  dropPosition: 'before' | 'inside' | 'after' | null = null;

  constructor(
    private departmentService: DepartmentService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.loadDepartmentTree();
  }

  loadDepartmentTree(): void {
    this.isLoading = true;
    this.departmentService.getDepartmentTree().subscribe(data => {
      console.log('部門樹狀數據:', JSON.stringify(data));
      this.dataSource.data = data;
      // 預設展開第一層節點
      if (data.length > 0) {
        this.expandedNodes.add(data[0].id);
      }
      this.isLoading = false;
    });
  }

  // 切換節點展開/收起狀態
  toggleNodeExpansion(node: DepartmentNode): void {
    if (this.isNodeExpanded(node)) {
      this.expandedNodes.delete(node.id);
    } else {
      this.expandedNodes.add(node.id);
    }
  }

  // 檢查節點是否展開
  isNodeExpanded(node: DepartmentNode): boolean {
    return this.expandedNodes.has(node.id);
  }

  // 將 DepartmentNode 轉換為 FlatDepartmentNode，以使用現有的 selectDepartment 方法
  convertToFlatNode(node: DepartmentNode): FlatDepartmentNode {
    return {
      expandable: node.children && node.children.length > 0,
      name: node.name,
      level: node.level,
      id: node.id,
      userCount: node.userCount
    };
  }

  selectDepartment(node: FlatDepartmentNode): void {
    const department = this.departmentService.getDepartmentById(node.id);
    if (department) {
      this.selectedDepartment = department;
      this.departmentService.selectDepartment(department);
      this.loadDepartmentUsers(department.id);
      this.loadNonDepartmentUsers(department.id);
      this.editingDepartment = false;
      this.showAddUserForm = false;
    }
  }

  loadDepartmentUsers(departmentId: string): void {
    this.departmentService.getUsersForDepartment(departmentId).subscribe(users => {
      this.departmentUsers = users;
    });
  }

  loadNonDepartmentUsers(departmentId: string): void {
    this.departmentService.getUsersNotInDepartment(departmentId).subscribe(users => {
      this.nonDepartmentUsers = users;
    });
  }

  toggleAddDepartmentForm(): void {
    this.showAddDepartmentForm = !this.showAddDepartmentForm;
    if (this.showAddDepartmentForm) {
      this.newDepartmentName = '';
      this.newDepartmentParentId = null;
    }
  }

  addDepartment(): void {
    if (!this.newDepartmentName.trim()) return;

    const newDepartment: Omit<Department, 'id'> = {
      name: this.newDepartmentName.trim(),
      parentId: this.newDepartmentParentId,
      level: this.newDepartmentParentId ?
        (this.departmentService.getDepartmentById(this.newDepartmentParentId)?.level || 0) + 1 : 0
    };

    this.departmentService.createDepartment(newDepartment).subscribe(() => {
      this.newDepartmentName = '';
      this.showAddDepartmentForm = false;
      this.loadDepartmentTree();
      this.snackBar.open('部門新增成功', '關閉', { duration: 3000 });
    });
  }

  startEditDepartment(): void {
    if (this.selectedDepartment) {
      this.editingDepartment = true;
      this.editDepartmentName = this.selectedDepartment.name;
    }
  }

  saveEditDepartment(): void {
    if (this.selectedDepartment && this.editDepartmentName.trim()) {
      const updatedDept: Department = {
        ...this.selectedDepartment,
        name: this.editDepartmentName.trim()
      };

      this.departmentService.updateDepartment(updatedDept).subscribe(() => {
        this.selectedDepartment = updatedDept;
        this.editingDepartment = false;
        this.loadDepartmentTree();
        this.snackBar.open('部門名稱已更新', '關閉', { duration: 3000 });
      });
    }
  }

  cancelEditDepartment(): void {
    this.editingDepartment = false;
  }

  deleteDepartment(): void {
    if (!this.selectedDepartment) return;

    this.departmentService.deleteDepartment(this.selectedDepartment.id).subscribe(success => {
      if (success) {
        this.snackBar.open('部門已刪除', '關閉', { duration: 3000 });
        this.selectedDepartment = null;
        this.loadDepartmentTree();
      } else {
        this.snackBar.open('無法刪除有子部門的部門', '關閉', { duration: 3000 });
      }
    });
  }

  toggleAddUserForm(): void {
    this.showAddUserForm = !this.showAddUserForm;
    if (this.showAddUserForm) {
      this.selectedUserToAdd = '';
    }
  }

  addUserToDepartment(): void {
    if (this.selectedDepartment && this.selectedUserToAdd) {
      this.departmentService.addUserToDepartment(this.selectedUserToAdd, this.selectedDepartment.id).subscribe(() => {
        this.loadDepartmentUsers(this.selectedDepartment!.id);
        this.loadNonDepartmentUsers(this.selectedDepartment!.id);
        this.selectedUserToAdd = '';
        this.showAddUserForm = false;
        this.loadDepartmentTree();
        this.snackBar.open('使用者已新增至部門', '關閉', { duration: 3000 });
      });
    }
  }

  removeUserFromDepartment(userId: string): void {
    if (this.selectedDepartment) {
      this.departmentService.removeUserFromDepartment(userId, this.selectedDepartment.id).subscribe(() => {
        this.loadDepartmentUsers(this.selectedDepartment!.id);
        this.loadNonDepartmentUsers(this.selectedDepartment!.id);
        this.loadDepartmentTree();
        this.snackBar.open('使用者已從部門移除', '關閉', { duration: 3000 });
      });
    }
  }

  // 開始拖拉
  onDragStart(event: DragEvent, node: DepartmentNode): void {
    this.draggedNode = node;
    // 為拖拉操作設置數據
    if (event.dataTransfer) {
      event.dataTransfer.setData('text/plain', node.id);
      event.dataTransfer.effectAllowed = 'move';
    }
  }

  // 拖拉過程中
  onDragOver(event: DragEvent, targetNode: DepartmentNode): void {
    event.preventDefault();
    if (!this.draggedNode || targetNode.id === this.draggedNode.id) {
      this.allowDrop = false;
      this.dropPosition = null;
      return;
    }

    // 獲取滑鼠在目標元素中的位置
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const y = event.clientY - rect.top;
    const height = rect.height;

    // 根據滑鼠位置判斷放置位置：上方1/4區域為放置在前面，下方1/4區域為放置在後面，中間區域為放置在內部
    if (y < height * 0.25) {
      // 上方 - 放置在前面
      this.dropPosition = 'before';
      if (this.canDropAsSibling(this.draggedNode, targetNode)) {
        this.allowDrop = true;
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
      } else {
        this.allowDrop = false;
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'none';
      }
    } else if (y > height * 0.75) {
      // 下方 - 放置在後面
      this.dropPosition = 'after';
      if (this.canDropAsSibling(this.draggedNode, targetNode)) {
        this.allowDrop = true;
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
      } else {
        this.allowDrop = false;
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'none';
      }
    } else {
      // 中間 - 放置在內部
      this.dropPosition = 'inside';
      if (this.canDropInto(this.draggedNode, targetNode)) {
        this.allowDrop = true;
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
      } else {
        this.allowDrop = false;
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'none';
      }
    }
  }

  // 結束拖拉
  onDrop(event: DragEvent, targetNode: DepartmentNode): void {
    event.preventDefault();
    if (!this.draggedNode || !this.allowDrop || !this.dropPosition) {
      this.resetDragState();
      return;
    }

    const draggedDepartment = this.departmentService.getDepartmentById(this.draggedNode.id);
    if (!draggedDepartment) {
      this.resetDragState();
      return;
    }

    // 獲取原父部門名稱
    let fromParentName = '頂級部門';
    if (draggedDepartment.parentId) {
      const parentDept = this.departmentService.getDepartmentById(draggedDepartment.parentId);
      if (parentDept) {
        fromParentName = parentDept.name;
      }
    }

    let confirmMessage = '';
    let operation = '';

    // 根據放置位置執行不同操作
    if (this.dropPosition === 'inside') {
      // 更改父部門
      confirmMessage = `您確定要將「${this.draggedNode.name}」部門從「${fromParentName}」移動到「${targetNode.name}」之下嗎？`;
      operation = 'changeParent';
    } else {
      // 調整順序
      const targetParentName = targetNode.parentId ?
        this.departmentService.getDepartmentById(targetNode.parentId)?.name || '頂級部門' :
        '頂級部門';

      if (this.dropPosition === 'before') {
        confirmMessage = `您確定要將「${this.draggedNode.name}」部門移動到「${targetNode.name}」之前嗎？`;
      } else { // after
        confirmMessage = `您確定要將「${this.draggedNode.name}」部門移動到「${targetNode.name}」之後嗎？`;
      }
      operation = 'reorder';
    }

    if (confirm(confirmMessage)) {
      if (operation === 'changeParent') {
        // 更新母部門關係
        const updatedDept: Department = {
          ...draggedDepartment,
          parentId: targetNode.id,
          level: targetNode.level + 1,
          order: 1000 // 暫時給一個大數字，放到最後
        };

        this.departmentService.updateDepartment(updatedDept).subscribe(() => {
          this.loadDepartmentTree();
          this.snackBar.open('部門結構已更新', '關閉', { duration: 3000 });
        });
      } else {
        // 調整順序 - 需要獲取目標元素的當前順序和相鄰元素
        let targetParentId = targetNode.parentId; // 獲取目標節點的父ID
        const siblings = this.getSiblingsOfNode(targetNode);

        // 確定新的順序值
        let newOrder = 0;
        const targetIndex = siblings.findIndex(s => s.id === targetNode.id);

        if (this.dropPosition === 'before') {
          if (targetIndex > 0) {
            // 如果目標前面有元素，取目標與前一個元素順序的平均值
            newOrder = (siblings[targetIndex].order + (siblings[targetIndex - 1]?.order || 0)) / 2;
          } else {
            // 如果目標是第一個，則設置更小的值
            newOrder = siblings[targetIndex].order - 10;
          }
        } else { // after
          if (targetIndex < siblings.length - 1) {
            // 如果目標後面有元素，取目標與後一個元素順序的平均值
            newOrder = (siblings[targetIndex].order + (siblings[targetIndex + 1]?.order || 0)) / 2;
          } else {
            // 如果目標是最後一個，則設置更大的值
            newOrder = siblings[targetIndex].order + 10;
          }
        }

        // 更新拖動元素的父ID和順序
        const updatedDept: Department = {
          ...draggedDepartment,
          parentId: targetParentId,
          level: targetNode.level,
          order: newOrder
        };

        this.departmentService.updateDepartment(updatedDept).subscribe(() => {
          this.loadDepartmentTree();
          this.snackBar.open('部門順序已更新', '關閉', { duration: 3000 });
        });
      }
    }

    this.resetDragState();
  }

  // 重置拖拉狀態
  resetDragState(): void {
    this.draggedNode = null;
    this.allowDrop = false;
    this.dropPosition = null;
  }

  // 拖拉結束
  onDragEnd(): void {
    this.resetDragState();
  }

  // 檢查是否可以將一個部門放入目標部門中
  canDropInto(dragged: DepartmentNode, target: DepartmentNode): boolean {
    // 不能拖放到自己
    if (dragged.id === target.id) return false;

    // 檢查是否嘗試將父部門放入其子部門
    let currentNode = target;
    const visitedIds = new Set<string>();

    while (currentNode) {
      visitedIds.add(currentNode.id);
      // 獲取父節點
      const parentDept = this.departmentService.getDepartmentById(currentNode.id);
      if (!parentDept || !parentDept.parentId || visitedIds.has(parentDept.parentId)) break;

      const parentNode = this.findNodeById(this.dataSource.data, parentDept.parentId);
      if (!parentNode) break;

      // 如果父節點是被拖動的節點，則不允許放置
      if (parentNode.id === dragged.id) return false;

      currentNode = parentNode;
    }

    return true;
  }

  // 檢查是否可以將一個部門放在目標部門的同一級別
  canDropAsSibling(dragged: DepartmentNode, target: DepartmentNode): boolean {
    // 不能放在自己前後
    if (dragged.id === target.id) return false;

    // 檢查父節點是否相同
    const draggedDept = this.departmentService.getDepartmentById(dragged.id);
    const targetDept = this.departmentService.getDepartmentById(target.id);

    if (!draggedDept || !targetDept) return false;

    // 檢查是否已經是同一級別，或是否可以改變其父層級
    return true;
  }

  // 獲取節點的所有同級節點（包括自己）
  getSiblingsOfNode(node: DepartmentNode): {id: string, order: number}[] {
    // 首先嘗試在數據源中找到所有同級節點
    const parentId = node.parentId;
    const result: {id: string, order: number}[] = [];

    if (!parentId) {
      // 頂級節點
      this.dataSource.data.forEach(item => {
        result.push({id: item.id, order: item.order || 0});
      });
    } else {
      // 查找父節點
      const parentNode = this.findNodeById(this.dataSource.data, parentId);
      if (parentNode && parentNode.children) {
        parentNode.children.forEach(item => {
          result.push({id: item.id, order: item.order || 0});
        });
      }
    }

    // 按順序排序
    return result.sort((a, b) => a.order - b.order);
  }

  // 通過ID查找節點
  findNodeById(nodes: DepartmentNode[], id: string): DepartmentNode | null {
    for (const node of nodes) {
      if (node.id === id) return node;

      if (node.children && node.children.length > 0) {
        const found = this.findNodeById(node.children, id);
        if (found) return found;
      }
    }
    return null;
  }


}
